import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Scale, ArrowLeft, FileText } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="glass-card mx-6 my-4 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <Scale className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-blue-900">Advokat for alle</h1>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <FileText className="w-12 h-12 text-blue-600" />
            <div>
              <h2 className="text-4xl font-bold text-blue-900">Vilkår for bruk</h2>
              <p className="text-gray-600 mt-2">Sist oppdatert: Januar 2025</p>
            </div>
          </div>

          <div className="space-y-8">
            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">1. Aksept av vilkår</h3>
              <p className="text-gray-700">
                Ved å bruke Advokat for alle aksepterer du disse vilkårene fullt ut. Hvis du ikke er enig i vilkårene, skal du ikke bruke tjenesten.
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">2. Tjenestebes krivelse</h3>
              <p className="text-gray-700 mb-3">
                Advokat for alle er en plattform som kobler klienter med lisensierte advokater. Tjenesten inkluderer:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• AI-assistert innsamling av juridisk informasjon</li>
                <li>• Utarbeidelse av juridiske analyser ved hjelp av AI (GPT-5)</li>
                <li>• Gjennomgang og godkjenning av alle svar av lisensiert advokat</li>
                <li>• Levering av juridisk rådgivning bekreftet av advokat</li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">3. Brukerens ansvar</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Du må være minst 18 år eller ha samtykke fra foresatte</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Du er ansvarlig for å gi korrekt og fullstendig informasjon</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Du skal ikke bruke tjenesten til ulovlige formål</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Du er ansvarlig for å holde kontoinformasjonen konfidensiell</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">4. AI-bruk og ansvarsfraskrivelse</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Viktig:</strong> Vi bruker kunstig intelligens (GPT-5) som et internt verktøy for å utarbeide juridiske analyser.
                </p>
                <p>
                  <strong>Kvalitetssikring:</strong> Alle AI-genererte svar gjennomgås, redigeres og godkjennes av en lisensiert advokat før de sendes til deg.
                </p>
                <p>
                  <strong>Juridisk rådgivning:</strong> Kun svar som er eksplisitt merket "Bekreftet av advokat" med navn og tidsstempel utgjør juridisk rådgivning.
                </p>
              </div>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">5. Betaling og refusjon</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Priser er oppgitt i norske kroner (NOK)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Betaling foretas via Stripe (kredittkort) eller Vipps</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Refusjon gis hvis saken avvises i triageringsfasen</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Etter at advokat har godkjent og levert svar, gis normalt ikke refusjon</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">6. Konfidensialitet</h3>
              <p className="text-gray-700 mb-3">
                Vi er forpliktet til å behandle all informasjon du deler med oss konfidensielt:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• Advokater er bundet av taushetsplikt</li>
                <li>• All data er kryptert under overføring og ved lagring</li>
                <li>• Tilgang til data er strengt begrenset og logges</li>
                <li>• Se personvernerklæringen for mer informasjon</li>
              </ul>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">7. Ansvarsbegrensning</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Advokat for alle og tilknyttede advokater er ansvarlig for juridisk rådgivning som leveres, men:
                </p>
                <ul className="space-y-2">
                  <li>• Vi garanterer ikke spesifikke resultater i juridiske saker</li>
                  <li>• Du er ansvarlig for å følge opp rådene og ta egne beslutninger</li>
                  <li>• For komplekse saker anbefaler vi oppfølging med fysisk konsultasjon</li>
                  <li>• Ansvar er begrenset til beløpet betalt for tjenesten</li>
                </ul>
              </div>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">8. Endringer i vilkår</h3>
              <p className="text-gray-700">
                Vi forbeholder oss retten til å endre disse vilkårene. Endringer vil bli publisert på denne siden med oppdatert dato. Ved vesentlige endringer vil du bli varslet via e-post.
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">9. Gjeldende lov og verneting</h3>
              <p className="text-gray-700">
                Disse vilkårene er underlagt norsk lov. Eventuell tvist skal løses ved de ordinære domstoler i Norge.
              </p>
            </Card>

            <Card className="p-8">
              <h3 className="text-2xl font-semibold text-blue-900 mb-4">10. Kontakt</h3>
              <p className="text-gray-700 mb-2">
                Ved spørsmål om disse vilkårene, kontakt oss:
              </p>
              <p className="text-gray-700">
                <strong>E-post:</strong>{' '}
                <a href="mailto:kontakt@advokatforalle.no" className="text-blue-600 hover:underline">
                  kontakt@advokatforalle.no
                </a>
              </p>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;